import { graphql } from 'gatsby';
import { startTransition, useCallback, useContext, useEffect } from 'react';
import Cookie from 'js-cookie';

import { GeneralContext } from '@/context/GeneralContext';
import useLinkProps from '@/components/Link/hooks/useLinkProps';
import CloseIcon from '@/components/Icon/components/CloseIcon/CloseIcon';
import {
  StyledLink,
  StyledInformationStripe,
  StyledTitle,
  Wrapper,
  CloseButtonWrapper,
} from './InformationStripe.styles';

const COOKIE_HIDE_INFORMATION_STRIPE = 'HideInformationStripe';

const InformationStripe = ({ elements, system }: Queries.HEADER_INFORMATION_STRIPEFragment) => {
  const { title, link } = elements;
  const linkElements = link.value.linkElements;

  const { setIsInformationStripeVisible } = useContext(GeneralContext);

  const { linkChildren, linkProps } = useLinkProps({
    action: linkElements.action.value,
    target: linkElements?.target?.value,
    event: linkElements?.event?.value,
  });

  const handleHideInformationStripe = useCallback(() => {
    Cookie.set(COOKIE_HIDE_INFORMATION_STRIPE, system.last_modified);
    setIsInformationStripeVisible(false);
  }, [setIsInformationStripeVisible, system.last_modified]);

  useEffect(() => {
    const cookieValue = Cookie.get(COOKIE_HIDE_INFORMATION_STRIPE);
    const isStripeClosedByUser = !!cookieValue;

    // NOTE Hide the stripe if the cookie value matches its last modified date
    if (cookieValue === system.last_modified) {
      startTransition(() => {
        setIsInformationStripeVisible(false);
      });
    }

    // NOTE Remove the cookie when the stripe is updated in Kontent and the stripe is currently hidden
    if (isStripeClosedByUser && cookieValue !== system.last_modified) {
      Cookie.remove(COOKIE_HIDE_INFORMATION_STRIPE);
    }
  }, [setIsInformationStripeVisible, system.last_modified]);

  return (
    <StyledInformationStripe data-block-information-stripe>
      <Wrapper>
        <CloseButtonWrapper
          onClick={handleHideInformationStripe}
          aria-label="Close information stripe"
        >
          <CloseIcon variant="close_icon_basic_light" />
        </CloseButtonWrapper>
        <StyledTitle>{title.value}</StyledTitle>
        <StyledLink {...linkProps}>
          {linkChildren}
          {linkElements.text.value}
        </StyledLink>
      </Wrapper>
    </StyledInformationStripe>
  );
};

export const fragment = graphql`
  fragment HEADER_INFORMATION_STRIPE on kontent_item_header_information_stripe {
    elements {
      title {
        value
      }
      link {
        value {
          ...LINK
        }
      }
    }
    system {
      last_modified
    }
  }
`;

export default InformationStripe;
